import { FormattedMessage, useIntl } from 'react-intl';
import { cn } from '@/helpers/classnames';
import { Fragment, ReactNode, useMemo, useState } from 'react';
import { DialogWindow } from './Dialog';
import { IconFilter, IconXmark } from '@/icons';
import IconButton from './core/IconButton';
import Button from './core/Button';

interface FilterPanelProps {
  firstInput: ReactNode;
  filterOptions?: ReactNode[];
  sortingOptions?: ReactNode[];
}

export function FilterPanel({
  firstInput: firstInput,
  sortingOptions,
  filterOptions,
}: FilterPanelProps) {
  const [isOpen, setIsOpen] = useState(false);

  const { formatMessage } = useIntl();

  const toggleOpen = () => setIsOpen((prev) => !prev);

  const dialogContent = useMemo(() => {
    return (
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-8">
          {filterOptions?.map((option, index) => (
            <Fragment key={index}>{option}</Fragment>
          ))}
        </div>
        {sortingOptions && (
          <div className="flex flex-col gap-3">
            <span className="font-bold text-text-800">
              <FormattedMessage defaultMessage="Sortera" />
            </span>
            {sortingOptions.map((option, index) => (
              <Fragment key={index}>{option}</Fragment>
            ))}
          </div>
        )}
        <Button
          variant="ghost"
          startIcon={<IconXmark />}
          onClick={toggleOpen}
          className="mt-6 w-full"
        >
          <FormattedMessage defaultMessage="Stäng" />
        </Button>
      </div>
    );
  }, [filterOptions, sortingOptions]);

  return (
    <>
      <span className="hidden font-bold text-text-800 md:block">
        <FormattedMessage defaultMessage="Filtrera" />
      </span>
      <div className="mt-4 flex flex-col gap-2 md:my-10 md:mb-8 md:gap-0">
        <div className="flex flex-row gap-2">
          {firstInput}
          <IconButton
            aria-label={formatMessage({ defaultMessage: 'Öppna filter' })}
            icon={<IconFilter />}
            className={cn('flex md:hidden')}
            onClick={toggleOpen}
          />
        </div>
        <DialogWindow
          title={formatMessage({ defaultMessage: 'Filtrera' })}
          onOpenChange={setIsOpen}
          open={isOpen}
          content={dialogContent}
        />
        <div className="hidden md:flex md:flex-col md:gap-8 md:rounded-none md:p-0 md:pt-8">
          {filterOptions?.map((option, index) => (
            <div key={index}>{option}</div>
          ))}
          {sortingOptions && (
            <div className="flex flex-col gap-3">
              <span className="font-bold text-text-800">
                <FormattedMessage defaultMessage="Sortering" />
              </span>
              {sortingOptions.map((option, index) => (
                <div key={index}>{option}</div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
